import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { Typography } from '@pesto/ui/components/Typography';
import { z } from 'zod';

import type { Loan } from '../../__generated__/graphql/api';
import type { LoanModalPayload, ModalProps } from '../types/modal.types';

import { useApplyDelinquencyMutation, useCureDelinquencyMutation } from '../../__generated__/graphql/api';
import { getLoanRefetchQuery } from '../../components/LoanDetails/getLoanRefetchQuery';
import { createFormComponent } from '../utils/create-form-component';

const schema = z.object({
  reason: z.string().optional(),
});

type DelinquencyForm = z.infer<typeof schema>;

export const ApplyDelinquencyComponent = ({
  payload,
  api,
}: ModalProps<LoanModalPayload & { isDelinquent?: Loan['isDelinquent'] }>) => {
  const { loanId } = payload;
  const isDelinquent = Boolean(payload.isDelinquent);
  const { handleMutation } = useMutationWithToast();
  const mutationOptions = getLoanRefetchQuery(loanId);

  const [applyDelinquency, { loading: applyLoading }] = useApplyDelinquencyMutation(mutationOptions);
  const [cureDelinquency, { loading: cureLoading }] = useCureDelinquencyMutation(mutationOptions);

  const loading = applyLoading || cureLoading;

  const handleSubmit = (data: DelinquencyForm) => {
    const variables = { loanId, reason: data.reason ?? '' };

    if (isDelinquent) {
      void handleMutation({
        mutation: () => cureDelinquency({ variables }),
        extractSuccess: (result) => !!result?.data?.cureLoanDelinquency,
        onSuccess: () => api.close(),
        debugLabel: 'cure-delinquency',
        toast: {
          success: 'Delinquency cured successfully',
          error: 'Failed to cure delinquency',
          loading: 'Curing delinquency...',
        },
      });
    } else {
      void handleMutation({
        mutation: () => applyDelinquency({ variables }),
        extractSuccess: (result) => !!result?.data?.applyLoanDelinquency,
        onSuccess: () => api.close(),
        debugLabel: 'apply-delinquency',
        toast: {
          success: 'Delinquency applied successfully',
          error: 'Failed to apply delinquency',
          loading: 'Applying delinquency...',
        },
      });
    }
  };

  const config: SmartFormConfig<DelinquencyForm> = {
    fields: [
      {
        name: 'reason',
        label: 'Reason',
        type: 'text',
        required: false,
      },
    ],
    schema,
    defaultValues: {},
    submitLabel: isDelinquent ? 'Cure Delinquency' : 'Apply Delinquency',
  };

  return (
    <div className="flex flex-col gap-4">
      <Typography variant="body" className="text-muted-foreground text-sm">
        {isDelinquent
          ? 'This will cure the delinquency status for this loan.'
          : 'This will mark the loan as delinquent.'}
      </Typography>
      {createFormComponent({
        config,
        handleSubmit,
        loading,
      })}
    </div>
  );
};
